'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Event fields needed to build the "add to Google Calendar" link. The slug
 * keys the persisted reminder state; the rest fills the calendar template.
 */
export interface ReminderEventData {
  title: string;
  /** ISO timestamp of when the event starts. */
  startAt: string;
  /** ISO timestamp of when the event ends; defaults to start + 1 hour. */
  endAt: string | null;
  /** IANA timezone of the event, e.g. "Asia/Jakarta". */
  timezone: string;
  /** Human-readable description (plain text or HTML; HTML is stripped). */
  description?: string | null;
}

/** localStorage key holding the array of event slugs the user is reminded of. */
const REMINDER_STORAGE_KEY = 'justisio:event-reminders';

/** Keeps the calendar description short so the generated URL stays sane. */
const DESCRIPTION_MAX_LENGTH = 500;

/** The two URL parts are separated by a literal "/" (unencoded). */
const DATE_SEPARATOR = '/';

/** How long a reminder event lasts when the event has no end time. */
const DEFAULT_EVENT_DURATION_MS = 60 * 60 * 1000;

/**
 * Reads the set of slugs the user has already set reminders for. Storage can
 * be unavailable (privacy mode) or corrupted by an older app version, so a
 * failure degrades to an empty set instead of breaking the button.
 */
function readRemindedSlugs(): Set<string> {
  try {
    const raw = window.localStorage.getItem(REMINDER_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((item): item is string => typeof item === 'string')
    );
  } catch (error) {
    console.error(
      '[useEventReminder] Failed to read reminder state from localStorage',
      error
    );
    return new Set();
  }
}

/** Appends the slug to the persisted reminder set. */
function persistReminder(slug: string): void {
  const slugs = readRemindedSlugs();
  slugs.add(slug);
  try {
    window.localStorage.setItem(
      REMINDER_STORAGE_KEY,
      JSON.stringify(Array.from(slugs))
    );
  } catch (error) {
    console.error(
      '[useEventReminder] Failed to persist reminder state to localStorage',
      error
    );
  }
}

/** Strips HTML tags so article content can be used as plain-text details. */
function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, ' ');
}

/**
 * Parses an event timestamp into a Date. Postgres `timestamptz` values come
 * back as "2026-08-20 14:18:00+07" (space separator, bare hour offset), a
 * format Safari cannot parse, so normalize it to ISO 8601 first.
 */
function parseEventDate(value: string): Date {
  const normalized = value
    .replace(' ', 'T')
    .replace(/\+(\d{2})$/, '+$1:00');
  return new Date(normalized);
}

/**
 * Formats an ISO timestamp as the compact "YYYYMMDDTHHMMSS" value that the
 * Google Calendar template URL expects, expressed in the event's timezone.
 * The timezone is passed separately via the `ctz` parameter so the invite
 * lands at the exact local time of the event.
 */
function formatGoogleCalendarDate(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(parseEventDate(iso));

  const value = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const pad = (part: string) => part.padStart(2, '0');
  return `${value('year')}${pad(value('month'))}${pad(value('day'))}T${pad(
    value('hour')
  )}${pad(value('minute'))}${pad(value('second'))}`;
}

/** Resolves the event end, defaulting to one hour after the start. */
function resolveEndAt(event: ReminderEventData): string {
  if (event.endAt) return event.endAt;
  const start = parseEventDate(event.startAt).getTime();
  return new Date(start + DEFAULT_EVENT_DURATION_MS).toISOString();
}

/** Collapses whitespace and truncates the description to a safe length. */
function normalizeDescription(description: string | null | undefined): string {
  if (!description) return '';
  const plain = stripHtml(description).replace(/\s+/g, ' ').trim();
  if (plain.length <= DESCRIPTION_MAX_LENGTH) return plain;
  return `${plain.slice(0, DESCRIPTION_MAX_LENGTH)}…`;
}

/**
 * Builds the "add event" URL for Google Calendar from the given event data.
 * Kept pure and exported so callers and tests can reuse it without a hook.
 */
export function buildGoogleCalendarUrl(event: ReminderEventData): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatGoogleCalendarDate(
      event.startAt,
      event.timezone
    )}${DATE_SEPARATOR}${formatGoogleCalendarDate(
      resolveEndAt(event),
      event.timezone
    )}`,
    details: normalizeDescription(event.description),
    ctz: event.timezone,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Manages the "Ingatkan Saya" reminder state for a single event.
 *
 * The reminded state persists in localStorage keyed by the event slug, so a
 * user who already set a reminder keeps the activated bell across refreshes.
 * The first activation opens Google Calendar in a new tab with a prefilled
 * event; later clicks are no-ops since the reminder is already set.
 */
export function useEventReminder(slug: string, event: ReminderEventData) {
  const [isReminded, setIsReminded] = useState(false);

  useEffect(() => {
    setIsReminded(readRemindedSlugs().has(slug));
  }, [slug]);

  const remind = useCallback(() => {
    if (isReminded) return;
    window.open(
      buildGoogleCalendarUrl(event),
      '_blank',
      'noopener,noreferrer'
    );
    persistReminder(slug);
    setIsReminded(true);
  }, [isReminded, event, slug]);

  return { isReminded, remind };
}
